import React, { useEffect, useRef, useState, useMemo } from "react";
import {ButtonBox, LoadingBox, ModelDialogBox, PrinterForGroupComponent, SelectBox, TextBox, ToastBox} from "../index";
import { LookupItemModel, LookupTypeEnum, ToastModel } from "../../models";
import { getLabelName, getLookUpItemValue, isArabicCurrentLanguage } from "../../utils";
import {
    getAllPrinters,
    getTransactionPrintingSettingsByUserId,
    saveTransactionPrintingSettings,
} from "../../serviceBroker/printerApiServiceBroker";
import { PrintersModelResult, PrintersTransactionsResult } from "../../models/printers/printersModel";
import { Checkbox } from "antd";
import { FieldArray, Form, Formik } from "formik";
import { getLookupByType } from "../../serviceBroker/lookupApiServiceBroker";


export const PrintingSettingsComponent = () => {
    //#region state
    const [userLookUps, setUserLookUps] = useState<any[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isUserChanged, setIsUserChanged] = useState(false);
    const [currentUser, setCurrentUser] = useState<string | null>("30");
    const [allPrinters, setAllPrinters] = useState<PrintersModelResult[]>([]);
    const [initialValues, setInitialValues] = useState<{ transactions: PrintersTransactionsResult[] }>({
        transactions: [],
    });
    const [toastModel, setToastModel] = useState<ToastModel>({ show: false });
    const usersSelectBoxMultiselectRef = useRef<any>();
    //#endregion
    //#region functions
    const handleSaveTransactionPrintingSettings = async (values: PrintersTransactionsResult[]) => {
        let request = { TransactionPrintingSettings: values };
        try {
            setIsSubmitting(true);
            await saveTransactionPrintingSettings(request);
            setIsSubmitting(false);
            setToastModel({
                ...toastModel,
                show: true,
                variant: "success",
            });
        } catch (error:any) {
            setIsSubmitting(false);
           setToastModel({
                ...toastModel,
                show: true,
                variant: "danger",
                body:getLabelName(error)
            });
        }
    };
    // Memoize printer options to avoid repeated computations
    const printerOptions = useMemo(() => {
        return allPrinters.map((printer) => ({
            value: printer.Name,
            name: printer.Name,
            nameAr: printer.Name,
        }));
    }, [allPrinters]);
    //#endregion
    //#region useEffect
    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch users and printers in parallel
                const [getAllUsers, printers] = await Promise.all([
                    getLookupByType(LookupTypeEnum.Users),
                    getAllPrinters(),
                ]);

                // Update state with the fetched data
                setUserLookUps([...getAllUsers]);
                setAllPrinters(printers.Result);
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };
        fetchData().then(() => {});
    }, []);

    useEffect(() => {
        const getPrintersTransactions = async () => {
            if (currentUser) {
                setIsUserChanged(true);
                const printersTransactions = await getTransactionPrintingSettingsByUserId(currentUser);
                setInitialValues({ transactions: printersTransactions.Result });
                setIsUserChanged(false);
            }
        };
        getPrintersTransactions().then(() => {});
    }, [currentUser]);

    useEffect(() => {
        const getPrintersTransactions = async () => {
            if (currentUser) {
                const printersTransactions = await getTransactionPrintingSettingsByUserId(currentUser);
                setInitialValues({ transactions: printersTransactions.Result });
            }
        };
        getPrintersTransactions().then(() => {});
    }, [isSubmitting]);
    //#endregion
    //#region html
    return (
        <>
            {toastModel.show && (
                <ToastBox
                    isShown={toastModel.show}
                    header={toastModel.header}
                    body={toastModel.body}
                    variant={toastModel.variant}
                    delayDuration={toastModel.delayDuration}
                    onCloseEvent={() => {
                    setToastModel({ ...toastModel, show: false });
                    }}
                />
            )}
            {/* Show loading spinner if submitting or user is being changed */}
            {(isSubmitting || isUserChanged) ? (
                <LoadingBox />
            ) : (
                <>
                    {/* User selection */}
                    <div className="main-content"> {/* or your central wrapper div */}
                        <div
                            className="w-100 py-2"
                            style={{
                                position: "sticky",
                                top: 0,
                                zIndex: 10,
                                backgroundColor: "transparent",
                                borderBottom: "none",
                            }}
                        >
                            <div className="row d-flex gap-2 align-items-center">
                                <div className="col-sm-4 col-md-4">
                                    <SelectBox
                                        labelName={getLabelName("User")}
                                        isSingleSelect={true}
                                        selectedValues={currentUser ? [currentUser] : [""]}
                                        source={userLookUps}
                                        onStatusChange={(e: LookupItemModel) => {
                                            const val = getLookUpItemValue(e);
                                            setCurrentUser(val);
                                        }}
                                        multiselectRef={usersSelectBoxMultiselectRef}
                                    />
                                </div>
                                <div className="col-sm-4 col-md-4">
                                    <ButtonBox
                                        form="printing-settings-form"
                                        type="submit"
                                        size="lg"
                                        className="btn py-6"
                                    >
                                        {getLabelName("save")}
                                    </ButtonBox>
                                </div>
                            </div>
                        </div>

                    {/* Render form if transactions exist */}
                    {initialValues.transactions.length > 0 ? (
                        <Formik
                            initialValues={initialValues}
                            enableReinitialize
                            onSubmit={async (values) => {
                                let updatedValues = values.transactions.map((transaction) => ({
                                    ...transaction,
                                    rowState: 2,
                                }));
                                await handleSaveTransactionPrintingSettings(updatedValues);
                            }}
                        >
                            {({ values, setFieldValue }) => (
                                <Form id="printing-settings-form">
                                    <FieldArray name="transactions">
                                        {({ push }) => (
                                            <div>
                                                {values.transactions.map((transaction, index) => (
                                                    <TransactionRow
                                                        key={transaction.ID}
                                                        transaction={transaction}
                                                        index={index}
                                                        setFieldValue={setFieldValue}
                                                        printerOptions={printerOptions}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                    </FieldArray>
                                    {/* <ButtonBox type="submit">{getLabelName("save")}</ButtonBox> */}
                                </Form>
                            )}
                        </Formik>
                    ) : (
                        <div className="text-center">{getLabelName("no data")}</div>
                    )}
                    </div>
                </>

            )}
        </>
    );
    //#endregion
};
//#region  Reusable  component
const TransactionRow = React.memo(
    ({
         transaction,
         index,
         setFieldValue,
         printerOptions,
     }: {
        transaction: PrintersTransactionsResult;
        index: number;
        setFieldValue: (
            field: string,
            value: any,
            shouldValidate?: boolean | undefined
        ) => void;
        printerOptions: { value: string; name: string; nameAr: string }[];
    }) => {
        //#region state
        const isArabic = isArabicCurrentLanguage();
        const [showPrinterGroup, setShowPrinterGroup] = useState(false)
//#endregion
        //#region html
        return (
            <div key={transaction.ID} className="container-fluid bg-white border border-dark py-2 px-4 mb-2">
            <h3>
                    {
                        isArabic ?
                            (transaction.TransactionTypeNameAr)
                            :
                            (transaction.TransactionTypeNameEn)
                    }
                </h3>

                {/* Direct Print Settings */}
                <div className="row d-flex justify-content-between align-items-center mb-2">
                    <CheckboxSection
                        label={getLabelName("Direct Print")}
                        checked={transaction.IsDirectPrint}
                        onChange={() => setFieldValue(`transactions.${index}.IsDirectPrint`, !transaction.IsDirectPrint)}
                    />
                    <SelectBox
                        labelName=""
                        inputName={`transactions.${index}.DirectPrinterName`}
                        isSingleSelect={true}
                        selectedValues={[transaction.DirectPrinterName]}
                        source={printerOptions}
                        onStatusChange={(e: LookupItemModel) => {
                            const val = getLookUpItemValue(e);
                            setFieldValue(`transactions.${index}.DirectPrinterName`, val);
                        }}
                    />
                    <TextBox
                        labelName={getLabelName("Num. Copy")}
                        placeHolder={getLabelName("Num. Copy")}
                        type="number"
                        inputName={`transactions.${index}.DirectPrintCopies`}
                        inputValue={transaction.DirectPrintCopies}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setFieldValue(`transactions.${index}.DirectPrintCopies`, Number(e.target.value))
                        }
                    />
                </div>

                {/* Preparing Order Settings */}
                {transaction.CanConfigurePreparingPrinter && (
                    <div className="row d-flex justify-content-between align-items-center mb-2">
                        <CheckboxSection
                            label={getLabelName("Preparing Order - طلب تجهيز")}
                            checked={transaction.IsPrintPreparingOrder}
                            onChange={() => setFieldValue(`transactions.${index}.IsPrintPreparingOrder`, !transaction.IsPrintPreparingOrder)}
                        />
                        <SelectBox
                            labelName=""
                            inputName={`transactions.${index}.PreparingOrderPrinterName`}
                            isSingleSelect={true}
                            selectedValues={[transaction.PreparingOrderPrinterName]}
                            source={printerOptions}
                            onStatusChange={(e: LookupItemModel) => {
                                const val = getLookUpItemValue(e);
                                setFieldValue(`transactions.${index}.PreparingOrderPrinterName`, val);
                            }}
                        />
                        <TextBox
                            labelName={getLabelName("Num. Copy")}
                            placeHolder={getLabelName("Num. Copy")}
                            type="number"
                            inputName={`transactions.${index}.PreparingOrderPrintCopies`}
                            inputValue={transaction.PreparingOrderPrintCopies}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                setFieldValue(`transactions.${index}.PreparingOrderPrintCopies`, Number(e.target.value))
                            }
                        />
                    </div>
                )}

                {/* Kitchen Printer Settings */}
                {transaction.CanConfigureKitchenPrinter && (
                    <>
                        <div className="row d-flex justify-content-between align-items-center mb-2">
                            <CheckboxSection
                                label={getLabelName("kitchen only")}
                                checked={transaction.IsPrintUseKitchenPrinter}
                                onChange={() => setFieldValue(`transactions.${index}.IsPrintUseKitchenPrinter`, !transaction.IsPrintUseKitchenPrinter)}
                            />
                            <SelectBox
                                labelName=""
                                inputName={`transactions.${index}.KitchenPrinterName`}
                                isSingleSelect={true}
                                selectedValues={[transaction.KitchenPrinterName]}
                                source={printerOptions}
                                onStatusChange={(e: LookupItemModel) => {
                                    const val = getLookUpItemValue(e);
                                    setFieldValue(`transactions.${index}.KitchenPrinterName`, val);
                                }}
                            />
                            <TextBox
                                labelName={getLabelName("Num. Copy")}
                                placeHolder={getLabelName("Num. Copy")}
                                type="number"
                                inputName={`transactions.${index}.KitchenPrinterCopies`}
                                inputValue={transaction.KitchenPrinterCopies}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    setFieldValue(`transactions.${index}.KitchenPrinterCopies`, Number(e.target.value))
                                }
                            />
                        </div>
                        <div className="row d-flex justify-content-between align-items-center mb-2">
                            <CheckboxSection
                                label={getLabelName("Print Every Related items by Group on Separate bill")}
                                checked={transaction.IsPrintGroupItemSeparateCopies}
                                onChange={() => setFieldValue(`transactions.${index}.IsPrintGroupItemSeparateCopies`, !transaction.IsPrintGroupItemSeparateCopies)}
                            />
                            <CheckboxSection
                                label={getLabelName("Print Item Depend of Printer of its' Category")}
                                checked={transaction.IsPrintUseGroupPrinterKitchen}
                                onChange={() => {
                                    if (!transaction.IsPrintUseGroupPrinterKitchen) {
                                        setShowPrinterGroup(true)
                                    }
                                    setFieldValue(`transactions.${index}.IsPrintUseGroupPrinterKitchen`, !transaction.IsPrintUseGroupPrinterKitchen)
                                }}
                            />
                            <ModelDialogBox
                                isModelVisible={showPrinterGroup}
                                isCloseButtonVisible={false}
                                title={getLabelName("Set Printer For Group")}
                                size="xl"
                                onCloseEvent={() => {
                                    setShowPrinterGroup(false)
                                    setFieldValue(`transactions.${index}.IsPrintUseGroupPrinterKitchen`, !transaction.IsPrintUseGroupPrinterKitchen)
                                }}
                                isEscapeCloseEnabled={true}
                                isXCloseButtonVisible={true}
                            >
                                <PrinterForGroupComponent
                                    setShowPrinterGroup={setShowPrinterGroup}
                                />
                            </ModelDialogBox>
                        </div>
                    </>
                )}
            </div>
        );
        //#endregion
    }
);
const CheckboxSection = React.memo(
    ({
         label,
         checked,
         onChange,
     }: {
        label: string;
        checked: boolean;
        onChange: () => void;
    }) => (
        <div className="col-sm-4 col-md-4 d-flex gap-3 align-items-center">
            <Checkbox checked={checked} onChange={onChange} id={label} />
            <label style={{ marginInlineStart: "8px" }} htmlFor={label}>
                {label}
            </label>
        </div>
    )
);
//#endregion